import React, { useCallback, useEffect, useState } from "react";
import { Pagination } from "../../../../common/pagination";
import ModernPagination from "../../../../common/pagination/ModernPagination";
import Heading from "../../../../common/element/Heading";
import ClientInput from "../../../../common/element/Input/clientInput";
import BtnPrimary from "../../../../common/element/Buttons/btnPrimary";
import { debounce } from "../../../../common/snippet/Debouncing";
import { setBreadcrumbs } from "../../../../redux/Admin/breadcrumbsSlice";
import { useDispatch, useSelector } from "react-redux";
import SupportedDocumentsList from "./SupportedDocuments";
import AddEditSupportedDocument from "./AddEdit";
import { getSupportedDocumentsList } from "../../../../redux/SupportedDocuments/supportedDocumentsSlice";
import { selectSupportedDocumentsList, selectSupportedDocumentsListLoading, selectSupportedDocumentsListTotal } from "../../../../redux/SupportedDocuments/supportedDocumentsSelectors";
import { selectUserData } from "../../../../redux/auth/authSelector";
import { ROLES } from "../../../../config/rolePermissions";
import {
  getEmployeeDocuments,
} from "../../../../redux/Employee/employeesSlice";
import {
  selectEmployeeDocuments,
  selectEmployeeDocumentsLoading,
} from "../../../../redux/Employee/employeesSelector";
import StatusSelect from '../../../../common/element/StatusSelect/StatusSelect';

const SupportedDocuments = () => {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [countData, setCountData] = useState(0);
  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;
  const [status, setStatus] = useState('false');

  const [getFilterData, SetFilterData] = useState({
    search: "",
  });

  const handleStatusChange = (val) => {
    setStatus(val);
    setfilterpage(1); // Reset to page 1 on status change
  };

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Supported Documents",
          url: "/supported-documents",
          home: "/companies",
        },
      ])
    );
  }, [dispatch]);

  // Admin selectors
  const adminDocuments = useSelector(selectSupportedDocumentsList);
  const adminDocumentsLoading = useSelector(selectSupportedDocumentsListLoading);
  const adminDocumentsTotal = useSelector(selectSupportedDocumentsListTotal);

  // Employee selectors
  const employeeDocumentsResponse = useSelector(selectEmployeeDocuments);
  const employeeDocumentsLoading = useSelector(selectEmployeeDocumentsLoading);

  // Extract employee documents data and pagination
  const employeeDocuments = employeeDocumentsResponse?.data || [];
  const employeePagination = employeeDocumentsResponse?.pagination || {};

  // Determine which data to use based on role
  const documents = userRole === ROLES.ADMIN ? adminDocuments : employeeDocuments;
  const documentsLoading = userRole === ROLES.ADMIN ? adminDocumentsLoading : employeeDocumentsLoading;
  const documentsTotal = userRole === ROLES.ADMIN
    ? adminDocumentsTotal
    : (employeePagination?.total || 0);

  const handleSearch = (e) => {
    SetFilterData((prevalue) => ({
      ...prevalue,
      [e.target.name]: e.target.value.trim(),
    }));
  };

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  // Fetch data based on role
  useEffect(() => {
    if (userRole === ROLES.ADMIN) {
      dispatch(getSupportedDocumentsList({
        page: filterpage,
        limit: filterlimit,
        search: getFilterData.search,
        archived: status // Include the status/archive filter
      }));
    } else {
      // For Employee/HR roles
      dispatch(getEmployeeDocuments({
        page: filterpage,
        limit: filterlimit,
        search: getFilterData.search
      }));
    }
  }, [dispatch, filterpage, filterlimit, getFilterData.search, userRole, status]);

  useEffect(() => {
    // Ensure both total count and limit are valid before calculating pages
    if (typeof documentsTotal === 'number' && documentsTotal >= 0 && filterlimit > 0) {
      const calculatedPages = Math.max(1, Math.ceil(documentsTotal / filterlimit));
      setCountData(documentsTotal);
      setTotalPages(calculatedPages);
    } else {
      // Fallback: use length of current list when total is missing or invalid
      const fallbackTotal = Array.isArray(documents) ? documents.length : 0;
      const calculatedPages = Math.max(1, Math.ceil(fallbackTotal / filterlimit));
      setCountData(fallbackTotal);
      setTotalPages(calculatedPages);
    }
  }, [documentsTotal, filterlimit, documents]);

  const handlePageChange = (page) => {
    setfilterpage(page);
  };

  const handleRefresh = () => {
    if (userRole === ROLES.ADMIN) {
      dispatch(getSupportedDocumentsList({
        page: filterpage,
        limit: filterlimit,
        search: getFilterData.search,
        archived: status // Include status on refresh
      }));
    } else {
      dispatch(getEmployeeDocuments({
        page: filterpage,
        limit: filterlimit,
        search: getFilterData.search
      }));
    }
  };

  return (
    <React.Fragment>
      <div className="flex justify-between ">
        <div className="w-80 h-10 text-start">
          <Heading>Supported Documents</Heading>
        </div>
        {userRole === ROLES.ADMIN && (
          <div className="flex justify-between mb-5">
            <div className="flex gap-2">
              <BtnPrimary
                onClick={() => setDialogOpen(true)}
                className="text-xs h-9 flex items-center justify-center px-3 gap-1"
              >
                Add Document
                <svg
                  width="21"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.712 4.167v11.666M4.878 10h11.667"
                    stroke="#fff"
                    strokeWidth="1.667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </BtnPrimary>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end mb-4">
        {userRole === ROLES.ADMIN && (
          <div className='me-3'>
            <StatusSelect
              value={status}
              onChange={handleStatusChange}
              placeholder="Select Status"
            />
          </div>
        )}
        <div className="input-wrapper">
          <div className="input-suffix-start left-3.5">
            <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name={"search"}
          />
        </div>
      </div>

      <SupportedDocumentsList
        data={documents}
        currentPage={filterpage}
        loading={documentsLoading}
        pageLimit={filterlimit}
        onRefresh={handleRefresh}
      />

      {userRole === ROLES.ADMIN && (
        <AddEditSupportedDocument
          dialogOpen={dialogOpen}
          onDialogClose={() => {
            setDialogOpen(false);
          }}
          mode="add"
          currentPage={filterpage}
          currentLimit={filterlimit}
          currentSearch={getFilterData.search}
          onAdded={handleRefresh}
        />
      )}

      <div className="flex justify-between items-center mt-3">
        <Pagination
          totalCount={countData}
          pageNumber={filterpage}
          displayPage={documents?.length || 0}
          UpdatePageLimit={setfilterlimit}
          UpdatePage={setfilterpage}
        />

        {countData > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={filterpage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default SupportedDocuments;